import { FC, useContext } from 'react';
import { Select, Tag, Tooltip } from 'antd';
import { useSnapshot } from 'valtio';
import { formActions, formStore } from '../../../store';
import { FieldTypeContext } from '../../../providers/FieldTypeContext';
import { useFormSplit } from '../../../use/form/useFormSplit';
import { resolveSplitLabel } from '@/services/resolveSplitLabel';
import type { FormSplitConfiguration } from '#types/System.types';

/**
 * Select that filters the form to one value of a split criterion, or to no split
 * at all. Groups the value options under "Filter to" and shows each option's row
 * count; stays enabled-but-empty when the form type has splitting off.
 *
 * Reads a narrow snapshot (ids, `selectedSplitOption`, split configuration) and
 * holds its own `useFormSplit` rather than `useFormStore()`, whose spread of
 * `formVersion.data` re-rendered this control on every keystroke in the form.
 * Nothing in this path reads field values, so it is keystroke-insensitive by
 * construction — do not reintroduce a `useFormStore()` read here.
 */
const FormSplitControl: FC = () => {
  const snap = useSnapshot(formStore.state);
  const { fieldTypes } = useContext(FieldTypeContext);

  const splitConfig = snap.formType.data?.splitConfiguration as FormSplitConfiguration | undefined;
  const selectedSplitOption = snap.selectedSplitOption;
  const arTableParentPath = snap.formId && snap.versionId
    ? `forms/${snap.formId}/formVersions/${snap.versionId}`
    : undefined;

  const { splitOptions, formattedOptions, splitOptionsMetadata, splitRowLimitWarning } = useFormSplit(
    splitConfig,
    fieldTypes,
    arTableParentPath,
    !!splitConfig?.enabled,
  );

  const isFiltered = selectedSplitOption !== 'no-split';
  const canSplit = splitConfig?.enabled && formattedOptions.length > 1;
  const splitByLabels = (splitConfig?.splitBy ?? [])
    .map((ref) => resolveSplitLabel(ref, fieldTypes))
    .filter(Boolean)
    .join(', ');
  const limitWarningText = splitRowLimitWarning
    ? `Split options limited to first ${splitRowLimitWarning.rowLimit} rows for: ${splitRowLimitWarning.tableIds.join(', ')}`
    : null;

  const bySuffix = splitByLabels ? ` by ${splitByLabels}` : '';
  const addTarget = splitByLabels || 'the form';

  return (
    <Tooltip title={
      isFiltered ? `Filtering form data${bySuffix}. Select 'No split' to show all records.`
        : canSplit ? `Select a split mode to filter the form data${bySuffix}.`
          : splitConfig?.enabled ? `Add values to ${addTarget} to enable splitting.`
            : "Form splitting is not enabled for this form."
    }
    >
      <Select
        value={selectedSplitOption}
        onChange={(value: string) => {
          formActions.setSelectedSplitOption(splitOptions.find((opt) => opt.value === value));
        }}
        style={{
          width: 180,
        }}
        status={isFiltered ? 'warning' : undefined}
        styles={{
          popup: {
            root: {
              width: 320
            }
          }
        }}
        placeholder="Select split mode"
        optionRender={(option) => {
          const metadata = splitOptionsMetadata.get(option.data.value as string);
          const recordCount = metadata?.recordCount;
          return (
            <>
              <span>{option.data.label}</span>
              &nbsp;
              {recordCount !== undefined && recordCount > 0 && (
                <Tag style={{ borderRadius: 100 }}>
                  {recordCount} item{recordCount === 1 ? '' : 's'}
                </Tag>
              )}
            </>
          );
        }}
        options={formattedOptions}
        aria-label="Form split control"
        disabled={!canSplit}
      />
      {limitWarningText ? (
        <Tooltip title={limitWarningText}>
          <Tag color="gold" style={{ borderRadius: 100 }} aria-label="Split options limited by row limit">
            Limited
          </Tag>
        </Tooltip>
      ) : null}
    </Tooltip>
  )
};

export default FormSplitControl;
